import { useState } from 'react';
import Image from 'next/image';
import clsx from 'clsx';
import Layout from '@/components/layout';
import Accordion from '@/components/common/Accordion';
import useWindowSize from '@/hooks/useWindowSize';
import { Pages } from '@/lib/seoData';
import { NextSSRPage, withServerSideProps } from 'hoc/withServerSideProps';

const faqSections = [
  { id: 'Membership' as SectionIds, title: 'Membership', subTitle: '' },
  { id: 'Account' as SectionIds, title: 'Account', subTitle: '' },
  { id: 'Orders' as SectionIds, title: 'Orders', subTitle: '' },
  { id: 'Shipping' as SectionIds, title: 'Shipping', subTitle: '' },
  { id: 'Payments' as SectionIds, title: 'Payments', subTitle: '' }
];

const faqs = {
  Membership: {
    faqs: [
      {
        question: 'What is Daily Polished?',
        answer:
          'Daily Polished is a members only luxurious beauty and lifestyle discovery and shopping platform where curious beauty lovers discover emerging brands and try full size beauty products at sample size prices usually at 60% off retail.'
      },
      {
        question: 'How does Daily Polished membership work?',
        answer: `Become Daily Polished member by choosing either a monthly or annual membership. Then login in daily to see a featured brand, learn about it and try it at 60% off.We are not a subscription box. We don’t force members to commit to anything they don’t want or need. We don’t sell wasteful sample size beauty products or unwanted stock or close outs.`
      },
      {
        question: 'What brands should I expect on Daily Polished?',
        answer: `We focus on discovery and are committed to showcasing the best emerging brands that are on the radar of leading beauty editors. We feature high quality prestige, mass-prestige and indie brands substantially all of which are clean beauty and also sustainable whenever possible.`
      },
      {
        question: 'Why is the number of units I can buy limited?',
        answer:
          'The number of units per SKU that you may purchase is limited both to ensure that all members have the opportunity to try new products, and also to create a safe platform for brands where products are not purchased for resale.'
      },
      {
        question: 'How much does a Daily Polished membership cost?',
        answer: `Members make back their monthly membership in one transaction. Daily Polished offers two types of memberships -  Monthly memberships at $8 per month and annual memberships for $60 per year.`
      },
      {
        question: 'How much will I save as a Daily Polished member?',
        answer:
          'We provide you the opportunity to experience full-size products at wholesale prices. We offer every member the unique opportunity to discover new brands and try them at 60% off retail.'
      },
      {
        question: 'Can I pause my membership?',
        answer:
          'We do not allow you to pause your membership. However, you may cancel your membership as described below.'
      },
      {
        question: 'How do I cancel my membership?',
        answer: `You may cancel your membership by logging in to your account and selecting “Cancel Membership” or emailing us at care@DailyPolished.com. Your cancellation will be effective beginning with the next billing period. Upon cancellation your access to Daily Polished membership benefits will continue until the end of your already paid membership period.`
      }
    ]
  },
  Account: {
    faqs: [
      {
        question: 'How do I view or edit my account?',
        answer:
          'Login into your account to view and edit your membership status, personal information, address, and payment method.'
      }
    ]
  },
  Orders: {
    faqs: [
      {
        question: 'I do not live in the U.S. Can I order online?',
        answer:
          'At this time, we are only accepting orders from U.S. residents to be shipped to U.S. addresses.'
      },
      {
        question: 'Can I modify or cancel my order?',
        answer:
          'Unfortunately, due to high demand, limited stock and fast processing times, we are unable to cancel or modify orders after they have been submitted.'
      },
      {
        question: 'How do I track my order?',
        answer:
          'You will receive a confirmation email when your order has been processed and shipped. Please allow up to 24-72 hours for tracking information to appear at the selected courier.'
      },
      {
        question: 'How long will it take to process my order?',
        answer: `We accept orders 24/7 and do our best to process each order as soon as possible. Our brand partners’ standard processing time is 24 to 72 hrs. Orders placed after 5pm ET will generally be processed the following day, and orders placed on Saturday, Sunday or a holiday will be processed on the following business day.`
      },
      {
        question: 'I think my order is lost. What do I do?',
        answer: `If you do not receive your order within 7 days of receiving confirmation, please contact the brand partner directly at the contact information on their website and provide your order confirmation number. In addition, please contact Daily Polished at care@dailypolished.com and include your order confirmation number in the email. If we confirm that your order was lost, we will have replacement products sent, and if not possible, you will receive a refund. Additionally, you can contact your local post office with your tracking number, and they can assist you with your missing order.`
      },
      {
        question: 'What do I do if my order arrived damaged?',
        answer: `If your order arrived damaged, please contact the brand partner directly using the contact information on their website. Make sure to send a photo of the damaged item along with your confirmation number. Please  also send it to care@DailyPolished.com. Once an email is received, the brand will respond with next steps and will replace the damaged product, or if not possible, provide a refund.`
      },
      {
        question: 'How do I return or exchange my order?',
        answer:
          'There are no returns or exchanges allowed. All sales are final.'
      }
    ]
  },
  Shipping: {
    faqs: [
      {
        question: 'Does Daily Polished ship outside the U.S.?',
        answer:
          'We only ship to residential and commercial addresses in the U.S. Unfortunately, we do not ship to P.O. boxes or internationally.'
      },
      {
        question: 'What are your shipping & handling rates?',
        answer:
          'Members pay for shipping because of our wholesale pricing. Shipping fees will vary based on the individual brand or products. Flat shipping fees will vary from $5 - $15 based on item’s size and weight and will be specified on the website.'
      }
    ]
  },
  Payments: {
    faqs: [
      {
        question: 'WHAT CREDIT CARDS DO YOU ACCEPT?',
        answer:
          'We accept all major credit cards including MasterCard, Visa, American Express, JCB, Discover, and Diners Club credit cards or debit cards. Unfortunately, unfortunately we cannot accept checks or money orders.'
      }
    ]
  }
};

type SectionIds = 'Membership' | 'Account' | 'Orders' | 'Shipping' | 'Payments';

const FAQPage: NextSSRPage = (props) => {
  const windowSize = useWindowSize()
  const isMobile = windowSize.width < 1024;
  const [selectedSubject, setSelectedSubject] =
    useState<SectionIds>('Membership');
  const [isSelectOpen, setIsSelectOpen] = useState(false);

  return (
    <Layout {...props}>
      <div className="flex flex-col lg:flex-row flex-grow h-full justify-center mx-auto">
        <div className="flex flex-col h-full w-full px-4 py-8 lg:py-16 lg:w-1/5 lg:pl-20">
          {!isMobile ? (
            faqSections.map(
              (section: { id: SectionIds; title: string }) => (
                <span
                  key={section.id}
                  onClick={() => setSelectedSubject(section.id)}
                  className={clsx(
                    'flex hover:underline text-metal uppercase my-2 cursor-pointer',
                    selectedSubject === section.id && 'underline'
                  )}
                >
                  {section.title}
                </span>
              )
            )
          ) : (
            <div className="relative">
              <div
                onClick={() => setIsSelectOpen(!isSelectOpen)}
                className="text-silver h-10 flex flex-row justify-between items-center px-4 border-solid border border-gray-200"
              >
                {
                  faqSections.filter(
                    (section: { id: SectionIds; title: string }) =>
                      section.id === selectedSubject
                  )[0].title
                }
                <div>
                  <Image
                    alt={'arrow-down'}
                    src={`/icons/arrow-down.svg`}
                    width={12}
                    height={8}
                  />
                </div>
              </div>
              <div className="absolute top-10 w-full bg-white z-10">
                {isSelectOpen &&
                  faqSections
                    .filter(
                      (section: { id: SectionIds; title: string }) =>
                        section.id !== selectedSubject
                    )
                    .map((sec) => (
                      <div
                        key={sec.id}
                        className="drop-shadow-sm h-10 flex flex-row items-center px-4 border-solid border border-gray-200 border-t-0 text-silver bg-white"
                        onClick={() => {
                          setSelectedSubject(sec.id);
                          setIsSelectOpen(false);
                        }}
                      >
                        {sec.title}
                      </div>
                    ))}
              </div>
            </div>
          )}
        </div>
        <div className="flex flex-col h-full py-4 w-full px-8 lg:py-16 lg:w-4/5 lg:px-20">
          <h1 className="m-0 h-0" style={{ visibility: 'hidden' }}>Frequently Asked Questions</h1>
          {faqs[selectedSubject].faqs.map((faq, index) => (
            <Accordion
              key={faq.answer}
              index={index}
              title={faq.question}
              content={faq.answer}
            />
          ))}
          <hr className="bg-[#DDDDDD]" />
        </div>
      </div>
    </Layout>
  );
};

export const getServerSideProps = withServerSideProps(async function () {
  return { props: {} }
}, Pages.FAQ)

export default FAQPage;
